// ABOUTME: Modal dialog for adding new family members via email invitation or direct account creation
// ABOUTME: Supports two modes: email invitation (sends invite link) and direct addition (creates account with password)

import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Typography,
  Box,
  Chip,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Clear as ClearIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useFamilyTerminology } from '../../../hooks/useFamilyTerminology';
import type { AddMemberForm } from '../types';
import type { RoleSuggestion } from '../../../types/api';

interface AddMemberModalProps {
  open: boolean;
  formData: AddMemberForm;
  firstNameError: string;
  lastNameError: string;
  memberEmailError: string;
  passwordError: string;
  confirmPasswordError: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  addingMember: boolean;
  roleSuggestions: RoleSuggestion[];
  loadingRoles: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  onFormChange: (updates: Partial<AddMemberForm>) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onMemberEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onToggleShowPassword: () => void;
  onToggleShowConfirmPassword: () => void;
  onLoadRoleSuggestions: () => Promise<void>;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  open,
  formData,
  firstNameError,
  lastNameError,
  memberEmailError,
  passwordError,
  confirmPasswordError,
  showPassword,
  showConfirmPassword,
  addingMember,
  roleSuggestions,
  loadingRoles,
  onClose,
  onSave,
  onFormChange,
  onFirstNameChange,
  onLastNameChange,
  onMemberEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onToggleShowPassword,
  onToggleShowConfirmPassword,
  onLoadRoleSuggestions
}) => {
  const { Term } = useFamilyTerminology();

  // Load role suggestions when modal opens
  useEffect(() => {
    if (open) {
      onLoadRoleSuggestions();
    }
  }, [open, onLoadRoleSuggestions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add {Term} Member</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel data-testid="invitation-method-selector">Invitation Method</InputLabel>
          <Select
            value={formData.invitationMethod}
            onChange={(e) => onFormChange({ invitationMethod: e.target.value as 'email' | 'direct' })}
            label="Invitation Method"
          >
            <MenuItem value="email">Email Invitation</MenuItem>
            <MenuItem value="direct">Direct Addition</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={(e) => onFirstNameChange(e.target.value)}
          error={!!firstNameError}
          helperText={firstNameError}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={(e) => onLastNameChange(e.target.value)}
          error={!!lastNameError}
          helperText={lastNameError}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Email"
          name="memberEmail"
          type="email"
          value={formData.memberEmail}
          onChange={(e) => onMemberEmailChange(e.target.value)}
          error={!!memberEmailError}
          helperText={memberEmailError}
          margin="normal"
        />

        <Box>
          <TextField
            fullWidth
            label="Role"
            value={formData.memberRole}
            onChange={(e) => onFormChange({ memberRole: e.target.value })}
            placeholder="e.g., Mom, Dad, Guardian..."
            margin="normal"
            InputProps={{
              endAdornment: formData.memberRole && (
                <IconButton
                  aria-label="clear"
                  onClick={() => onFormChange({ memberRole: '' })}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              )
            }}
          />

          {loadingRoles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Suggestions:
              </Typography>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mb: 1
              }}>
                {roleSuggestions.map((suggestion) => (
                  <Chip
                    key={suggestion.id}
                    label={suggestion.roleName}
                    onClick={() => onFormChange({ memberRole: suggestion.roleName })}
                    variant={formData.memberRole === suggestion.roleName ? 'filled' : 'outlined'}
                    color={formData.memberRole === suggestion.roleName ? 'primary' : 'default'}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: formData.memberRole === suggestion.roleName ? 'primary.dark' : 'action.hover'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          {!formData.memberRole && (
            <Typography variant="caption" color="error">
              Please select or type a role
            </Typography>
          )}
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              name="isPrimaryCaregiver"
              checked={formData.isPrimaryCaregiver}
              onChange={(e) => onFormChange({ isPrimaryCaregiver: e.target.checked })}
            />
          }
          label="Primary Caregiver"
        />

        {formData.invitationMethod === 'email' && (
          <>
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Invitation Message (for email invitation)
            </Typography>

            <TextField
              fullWidth
              label="Custom Message"
              value={formData.customMessage}
              onChange={(e) => onFormChange({ customMessage: e.target.value })}
              placeholder="Add a personal message to the invitation email..."
              margin="normal"
              multiline
              rows={2}
            />
          </>
        )}

        {formData.invitationMethod === 'direct' && (
          <>
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Account Setup (for direct addition)
            </Typography>

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => onPasswordChange(e.target.value)}
              error={!!passwordError}
              helperText={passwordError}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={onToggleShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => onConfirmPasswordChange(e.target.value)}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={onToggleShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Personal Information (optional)
            </Typography>

            <TextField
              fullWidth
              label="Personal Mantra"
              value={formData.mantra}
              onChange={(e) => onFormChange({ mantra: e.target.value })}
              placeholder="A phrase that inspires them..."
              margin="normal"
              multiline
              maxRows={2}
            />

            <TextField
              fullWidth
              label="How does your family know you love them?"
              value={formData.familyLoveQuestion}
              onChange={(e) => onFormChange({ familyLoveQuestion: e.target.value })}
              placeholder="Share how they show love to your family..."
              margin="normal"
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="What do you need most from your family?"
              value={formData.familyNeedQuestion}
              onChange={(e) => onFormChange({ familyNeedQuestion: e.target.value })}
              placeholder="What support do they need from your family?"
              margin="normal"
              multiline
              rows={2}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={!!firstNameError || !!lastNameError || !!memberEmailError || !!passwordError || !!confirmPasswordError || addingMember}
        >
          {addingMember ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMemberModal;
